import { Loader2, X } from 'lucide-react';

import { FileChip } from '@/components/file-chip';
import { cn } from '@/lib/utils';

export interface ChatAttachment {
  localId: string;
  filename: string;
  sizeBytes: number;
  status: 'uploading' | 'uploaded' | 'error';
  fileId?: string;
  error?: string;
}

interface ChatAttachmentsProps {
  attachments: ChatAttachment[];
  onRemove: (localId: string) => void;
  disabled?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ChatAttachments({
  attachments,
  onRemove,
  disabled,
}: ChatAttachmentsProps) {
  if (attachments.length === 0) return null;

  return (
    <div className='flex flex-wrap items-center gap-2 p-2'>
      {attachments.map((attachment) => {
        const isError = attachment.status === 'error';
        return (
          <FileChip
            key={attachment.localId}
            filename={attachment.filename}
            title={isError ? attachment.error : undefined}
            className={cn(
              'border shadow-md',
              isError
                ? 'border-destructive/50 bg-destructive/10 text-destructive'
                : 'border-sidebar-border',
            )}
            icon={
              attachment.status === 'uploading' ? (
                <Loader2
                  className='size-4 animate-spin text-white'
                  aria-label='Uploading'
                />
              ) : undefined
            }
            trailing={
              <>
                <span
                  className={cn(
                    'shrink-0 text-xs',
                    !isError && 'text-muted-foreground',
                  )}
                >
                  {isError ? 'failed' : formatBytes(attachment.sizeBytes)}
                </span>
                <button
                  type='button'
                  onClick={() => onRemove(attachment.localId)}
                  disabled={disabled}
                  aria-label={`Remove ${attachment.filename}`}
                  className='shrink-0 rounded-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50'
                >
                  <X className='size-3.5' />
                </button>
              </>
            }
          />
        );
      })}
    </div>
  );
}
